import { Fragment, FunctionComponent, ReactNode, useEffect, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";

import { Button } from "primereact/button";
import { Message } from "primereact/message";

import { translationKeys } from "../../../models/app-translation-keys";
import { useStore } from "../../../stores/StoreContext";

import "./GlobalErrorMessages.scss";

interface IGlobalErrorMessagesProps {
  children?: (content: ReactNode) => ReactNode;
}

const RawGlobalErrorMessages: FunctionComponent<IGlobalErrorMessagesProps> = (props: IGlobalErrorMessagesProps) => {
  const { t } = useTranslation();
  const { globalErrorsStore } = useStore();
  const errorContainerRef = useRef<HTMLDivElement | null>(null);
  const hasErrors = globalErrorsStore.hasErrors;

  useEffect(() => {
    if (!hasErrors) {
      globalErrorsStore.setErrorContainerHeight("0px");
    }
  }, [hasErrors]);

  useLayoutEffect(() => {
    if (!hasErrors) {
      return;
    }

    const updateHeight = () => {
      const rect = errorContainerRef.current?.getBoundingClientRect();
      globalErrorsStore.setErrorContainerHeight(`${rect?.height ?? 0}px`);
    };

    updateHeight();

    if (!errorContainerRef.current || typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(() => updateHeight());
    observer.observe(errorContainerRef.current);

    return () => observer.disconnect();
  }, [hasErrors, globalErrorsStore.currentErrors.length]);

  if (!hasErrors) {
    return;
  }

  props.children ??= content => content;

  return props.children(
    <div className="global-errors" ref={errorContainerRef}>
      {globalErrorsStore.currentErrors.map(({ key, data, allowClosing }) => (
        <Message
          key={key}
          severity="error"
          className="global-error"
          text={allowClosing ? undefined : (t(key, data) as string)}
          content={
            allowClosing ? (
              <Fragment>
                <span className="global-error-message-text">{t(key, data) as string}</span>
                <Button
                  icon="pi pi-times"
                  text
                  className="global-error-close-button"
                  onClick={() => globalErrorsStore.removeError(key)}
                  aria-label={t(translationKeys.actions.close)}
                />
              </Fragment>
            ) : undefined
          }
        />
      ))}
    </div>,
  );
};

export const GlobalErrorMessages = observer(RawGlobalErrorMessages);
